// epost-brief: reine Logik ohne Netz (Tests in logik.test.ts).
// Feldregeln laut E-POSTBUSINESS API v2.6.1 (https://api.epost.docuguide.com/swagger/v2/swagger.json).

export interface Empfaenger {
  zeilen: string[];        // Name, Zusatz, Straße … (max. 5, je 80 Zeichen; Zeile 5 nur Inland)
  plz: string;
  ort: string;
  land?: string;           // leer = Deutschland; sonst deutscher Ländername in GROSSBUCHSTABEN
}

export interface Absender {
  name: string;
  strasse: string;
  plz: string;
  ort: string;
}

export interface BriefOptionen {
  farbe?: boolean;
  duplex?: boolean;
  deckblatt?: boolean;     // Standard-Deckblatt der Post mit Anschriftfeld (Seite +1)
  einschreiben?: '' | 'Einwurf Einschreiben' | 'Einschreiben' | 'Einschreiben Rückschein';
  /** Post sammelt bis zur Mindestmenge (Mengenrabatt), spätestens freitags raus (Plugin UploadManagement). */
  sammelversand?: boolean;
  /** PREMIUMADRESS Basis: Post meldet neue Anschrift bei Unzustellbaren (kostenpflichtig, nicht mit Einschreiben). */
  premiumAdress?: boolean;
}

export const EINSCHREIBEN = ['Einwurf Einschreiben', 'Einschreiben', 'Einschreiben Rückschein'] as const;

const kuerze = (s: string, n: number) => s.trim().replace(/\s+/g, ' ').slice(0, n);

/** Prüft die Anschrift; liefert die Fehler in Klartext (leer = in Ordnung). */
export function pruefeEmpfaenger(e: Empfaenger): string[] {
  const fehler: string[] = [];
  const zeilen = (e.zeilen ?? []).map((z) => z?.trim() ?? '').filter(Boolean);
  if (zeilen.length === 0) fehler.push('Name des Empfängers fehlt.');
  const inland = !e.land?.trim() || ['DEUTSCHLAND', 'GERMANY', 'DE'].includes(e.land.trim().toUpperCase());
  if (zeilen.length > (inland ? 5 : 4)) fehler.push(`Höchstens ${inland ? 5 : 4} Anschriftzeilen erlaubt.`);
  if (!e.ort?.trim()) fehler.push('Ort fehlt.');
  if (inland && !/^\d{5}$/.test((e.plz ?? '').trim())) fehler.push('Postleitzahl muss fünfstellig sein.');
  return fehler;
}

/** Dateiname ohne Sonderzeichen (API-Vorgabe), eindeutig durch Zeitstempel, endet auf .pdf. */
export function sichererDateiname(wunsch: string, jetzt = new Date()): string {
  const basis = (wunsch || 'Brief').replace(/\.pdf$/i, '')
    .replace(/ä/g, 'ae').replace(/ö/g, 'oe').replace(/ü/g, 'ue').replace(/Ä/g, 'Ae').replace(/Ö/g, 'Oe').replace(/Ü/g, 'Ue').replace(/ß/g, 'ss')
    .replace(/[^A-Za-z0-9_-]+/g, '_').replace(/_+/g, '_').replace(/^_|_$/g, '') || 'Brief';
  const stempel = jetzt.toISOString().replace(/[-:T]/g, '').slice(0, 14);
  return `${basis.slice(0, 160)}_${stempel}.pdf`;
}

/** Baut das Letter-Objekt für POST /api/Letter. */
export function baueLetter(a: {
  pdfBase64: string; dateiname: string; empfaenger: Empfaenger; absender: Absender; optionen?: BriefOptionen;
  test: boolean; testEmail?: string; bezug?: string;
}): Record<string, unknown> {
  const o = a.optionen ?? {};
  const zeilen = a.empfaenger.zeilen.map((z) => kuerze(z ?? '', 80)).filter(Boolean);
  const land = (a.empfaenger.land ?? '').trim().toUpperCase();
  const letter: Record<string, unknown> = {
    fileName: a.dateiname,
    data: a.pdfBase64,
    isColor: !!o.farbe,
    isDuplex: !!o.duplex,
    coverLetter: !!o.deckblatt,
    testFlag: a.test,
    zipCode: kuerze(a.empfaenger.plz, 20),
    city: kuerze(a.empfaenger.ort, 80),
    country: ['DEUTSCHLAND', 'GERMANY', 'DE'].includes(land) ? '' : land,
    senderAdressLine1: kuerze(a.absender.name, 80),
    senderStreet: kuerze(a.absender.strasse, 80),
    senderZipCode: kuerze(a.absender.plz, 80),
    senderCity: kuerze(a.absender.ort, 80),
    activateDuplicateFailsafe: true,
    vendorSystemInformation: 'ImmoSchwede-App',
  };
  zeilen.forEach((z, i) => { letter[`addressLine${i + 1}`] = z; });
  if (a.test && a.testEmail) { letter.testEMail = a.testEmail; letter.testShowRestrictedArea = true; }
  if (o.einschreiben) letter.registeredLetter = o.einschreiben;
  const plugins: { plugInName: string; plugInModel: Record<string, unknown> }[] = [];
  if (o.sammelversand) plugins.push({ plugInName: 'UploadManagement', plugInModel: { UseMinimumQuantity: true, DueDayOfWeek: 'Fr' } });
  if (o.premiumAdress && !o.einschreiben && !land) plugins.push({ plugInName: 'PremiumAdress', plugInModel: { productVariants: 'basis' } });
  if (plugins.length) letter.plugInList = plugins;
  if (a.bezug) letter.custom1 = kuerze(a.bezug, 80);
  return letter;
}

/** statusID der API → Klartext für die App. */
export function statusText(statusId: number | null | undefined): string {
  switch (statusId) {
    case 1: return 'Angenommen';
    case 2: return 'Geprüft, wartet auf Druck';
    case 3: return 'Im Druckzentrum';
    case 4: return 'Versendet';
    case 99: return 'Fehler';
    case -1: return 'Abgebrochen';
    default: return 'Unbekannt';
  }
}

/** Status, bei denen sich nichts mehr ändert (keine weitere Abfrage nötig). */
export const istEndstatus = (statusId: number | null | undefined) => statusId === 4 || statusId === 99 || statusId === -1;
